/* =====================================================================
   emailApi.ts — the only place Email.tsx talks to the mail backend.
   Uses the tenant API key + the selected mailbox (X-Api-Key / X-Mailbox).
   API address: https://swiftgrid.cilbox.com (override with VITE_MAIL_API).
   ===================================================================== */

export const API_BASE: string = (import.meta as any).env?.VITE_MAIL_API ?? 'https://swiftgrid.cilbox.com';

export type ApiAtt = { id: string; name: string; size: number; type: string };
export type ApiMail = {
  id: string; subject: string; from: string; fromName: string | null;
  to: string[]; toNames: string[]; cc: string[]; ccNames: string[];
  date: string; snippet: string; body: string;
  isRead: boolean; isFlagged: boolean; attachments: ApiAtt[];
  folder: string; pinned: boolean; external: boolean; focused: boolean;
  categories: string[]; snoozeUntil: number | null;
};
export type ApiFolder = { id: string; label: string; unread: number; total: number; isSystem: boolean; isVirtual: boolean };
export type ApiList = { messages: ApiMail[]; total: number; page: number; pageSize: number; hasMore: boolean; othersUnread: number };
export type ApiDraft = { draftId: string; to: string[]; cc: string[]; bcc: string[]; subject: string; html: string; attachments: ApiAtt[] };
export type ComposePayload = {
  draftId?: string | null; mode: string; to: string[]; cc: string[]; bcc: string[];
  subject: string; html: string; inReplyTo?: string | null;
  attachmentsFrom?: string | null; keepAttachmentIds?: string[];
  attachments?: { name: string; contentType?: string; contentBase64: string }[];
};
export type ListParams = { folder: string; filter?: string | undefined; sort?: string | undefined; q?: string | undefined; category?: 'focused' | 'other' | undefined; page?: number | undefined; pageSize?: number | undefined };

/* ---------- session: tenant API key + selected mailbox (same as the rest of the app) ---------- */
let apiKey: string | null = null;
let mailbox: string | null = null;

/** Call whenever the API key or the active account changes. */
export const configureMail = (key: string | null, mailboxEmail: string | null) => {
  if (mailbox !== mailboxEmail || apiKey !== key) { clearMessageCache(); contactCache.clear(); }
  apiKey = key;
  mailbox = mailboxEmail ? mailboxEmail.trim().toLowerCase() : null;
};
export const isConfigured = () => !!apiKey && !!mailbox;

export class ApiError extends Error {
  constructor(public status: number, public code: string) { super(code); }
}

/** One request with X-Api-Key + X-Mailbox. */
async function raw(method: string, path: string, body?: unknown, _retry = true, signal?: AbortSignal): Promise<Response> {
  if (!apiKey || !mailbox) throw new ApiError(0, 'not_configured');
  const init: RequestInit = {
    method,
    headers: {
      'X-Api-Key': apiKey,
      'X-Mailbox': mailbox,
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  if (signal) init.signal = signal;
  const r = await fetch(`${API_BASE}${path}`, init);
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new ApiError(r.status, j.error ?? `http_${r.status}`);
  }
  return r;
}

const inflight = new Map<string, Promise<any>>();
/** Identical GETs made at the same time share one request (only when no AbortSignal is used). */
function call<T = any>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const run = async (): Promise<T> => {
    const r = await raw(method, path, body, true, signal);
    return r.status === 204 ? (undefined as T) : r.json().catch(() => undefined as T);
  };
  if (method !== 'GET' || signal) return run();
  const key = `${mailbox ?? ''}|${path}`;
  const hit = inflight.get(key);
  if (hit) return hit;
  const p = run().finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

/** True when a request was cancelled on purpose (ignore these in Email.tsx). */
export const isAbort = (e: unknown) => (e as any)?.name === 'AbortError';

/* ---------- folders ---------- */
export const getFolders = () => call<ApiFolder[]>('GET', '/api/webmail/folders');
export const createFolder = (name: string) => call<ApiFolder>('POST', '/api/webmail/folders', { name });
export const deleteFolder = (id: string) => call('DELETE', `/api/webmail/folders/${encodeURIComponent(id)}`);
export const emptyFolder = (id: string) => call<{ removed: number }>('POST', `/api/webmail/folders/${encodeURIComponent(id)}/empty`);

/* ---------- messages ---------- */
export const listMessages = (p: ListParams, signal?: AbortSignal) => {
  const qs = new URLSearchParams();
  Object.entries(p).forEach(([k, v]) => { if (v !== undefined && v !== '') qs.set(k, String(v)); });
  return call<ApiList>('GET', `/api/webmail/messages?${qs}`, undefined, signal);
};

/* Recent opened messages (max 30). Cleared whenever a message changes, moves or is deleted. */
const bodyCache = new Map<string, ApiMail>();
export const clearMessageCache = (ids?: string[]) => (ids ? ids.forEach((i) => bodyCache.delete(i)) : bodyCache.clear());
export const getMessage = async (id: string, signal?: AbortSignal, fresh = false) => {
  if (!fresh) {
    const hit = bodyCache.get(id);
    if (hit) return hit;
  }
  const m = await call<ApiMail>('GET', `/api/webmail/messages/${id}`, undefined, signal);
  bodyCache.set(id, m);
  if (bodyCache.size > 30) bodyCache.delete(bodyCache.keys().next().value as string);
  return m;
};
export const unblockImages = (id: string) => call<{ body: string }>('POST', `/api/webmail/messages/${id}/unblock`);

/** Downloads through fetch (needs the Authorization header), then saves the blob. */
async function saveBlob(path: string, fallbackName: string) {
  const r = await raw('GET', path);
  const cd = r.headers.get('Content-Disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd);
  const name = m ? decodeURIComponent(m[1] ?? m[2] ?? fallbackName) : fallbackName;
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
export const downloadAttachment = (messageId: string, attId: string, name: string) =>
  saveBlob(`/api/webmail/messages/${messageId}/attachments/${attId}`, name);
export const downloadEml = (messageId: string) => saveBlob(`/api/webmail/messages/${messageId}/raw`, `message-${messageId}.eml`);

/* ---------- actions (all take id lists) ---------- */
const A = '/api/webmail/actions';
const act = <T = any>(path: string, body: { ids: string[] } & Record<string, unknown>) => { clearMessageCache(body.ids); return call<T>('POST', path, body); };
export const setRead = (ids: string[], isRead: boolean) => act(`${A}/read`, { ids, isRead });
export const setFlag = (ids: string[], isFlagged: boolean) => act(`${A}/flag`, { ids, isFlagged });
export const setPin = (ids: string[], pinned: boolean) => act(`${A}/pin`, { ids, pinned });
export const setFocus = (ids: string[], focused: boolean) => act(`${A}/focus`, { ids, focused });
export const snooze = (ids: string[], untilMs: number | null) => act(`${A}/snooze`, { ids, untilMs });
export const setCategory = (ids: string[], category: string, on: boolean) => act(`${A}/category`, { ids, category, on });
export const moveMessages = (ids: string[], folder: string) => act(`${A}/move`, { ids, folder });
export const archiveMessages = (ids: string[]) => act(`${A}/archive`, { ids });
export const deleteMessages = (ids: string[]) => act<{ moved: number; purged: number }>(`${A}/delete`, { ids });
export const ignoreConversation = (id: string) => call('POST', `${A}/ignore`, { id });

/* ---------- senders / rules ---------- */
export const blockSender = (sender: string) => call('POST', '/api/webmail/senders/block', { sender });
export const setSenderList = (sender: string, list: 'Safe' | 'Quiet', on: boolean) => call('POST', '/api/webmail/senders/list', { sender, list, on });
export const sweep = (folder: string, sender: string, mode: 'all' | 'old' | 'keep') => call('POST', '/api/webmail/senders/sweep', { folder, sender, mode });
export const addRule = (folder: string, sender: string, action: 'archive' | 'junk' | 'delete') => call('POST', '/api/webmail/senders/rule', { folder, sender, action });
export const getRules = () => call<any[]>('GET', '/api/webmail/rules');
export const deleteRule = (id: number | string) => call('DELETE', `/api/webmail/rules/${id}`);

/* ---------- compose ---------- */
export const saveDraftApi = (p: ComposePayload) => call<{ draftId: string }>('POST', '/api/webmail/drafts', p);
export const getDraft = (id: string) => call<ApiDraft>('GET', `/api/webmail/drafts/${id}`);
export const discardDraft = (id: string) => call('DELETE', `/api/webmail/drafts/${id}`);
export const sendMail = (p: ComposePayload) => call<{ queued: number; messageId: string; sentCopyId: string | null }>('POST', '/api/webmail/send', p);

/* ---------- contacts ---------- */
const contactCache = new Map<string, { name: string; email: string }[]>();
export const searchContacts = async (q: string, signal?: AbortSignal) => {
  const key = q.trim().toLowerCase();
  const hit = contactCache.get(key);
  if (hit) return hit;
  const r = await call<{ name: string; email: string }[]>('GET', `/api/webmail/contacts?q=${encodeURIComponent(key)}`, undefined, signal);
  contactCache.set(key, r);
  if (contactCache.size > 100) contactCache.delete(contactCache.keys().next().value as string);
  return r;
};

/** File → base64 (no data: prefix) for ComposePayload.attachments. */
export const fileToBase64 = (f: File) =>
  new Promise<string>((res, rej) => {
    const fr = new FileReader();
    fr.onload = () => res(String(fr.result).split(',')[1] ?? '');
    fr.onerror = () => rej(fr.error);
    fr.readAsDataURL(f);
  });